// Documents page — upload only, content managed in Knowledge Base
import { DocumentUpload } from '@/components/documents/DocumentUpload'
import { useDocuments } from '@/hooks/useDocuments'
import { useCustomers } from '@/hooks/useCustomers'
import { PIIApprovalModal } from '@/components/pii/PIIApprovalModal'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircle, Upload } from 'lucide-react'
import { useState } from 'react'

export function Documents(): JSX.Element {
  const [selectedCustomerId, setSelectedCustomerId] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState(false)

  const { customers, isLoading: customersLoading } = useCustomers()
  const {
    upload, isUploading, uploadError,
    piiResult, piiScanError, uploadAfterApproval, cancelPiiReview, retryPiiScan, isScanning,
  } = useDocuments(selectedCustomerId || undefined)

  const handleUpload = async (data: Parameters<typeof upload>[0]) => {
    await upload(data)
    setUploadSuccess(true)
    setTimeout(() => setUploadSuccess(false), 5000)
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-kaycoo-teal-dark">
          <Upload className="h-6 w-6" />
          Upload Document
        </h1>
        <p className="text-sm text-kaycoo-charcoal-2">
          Upload D365 F&O documentation. After PII review and processing, documents are automatically added to the Knowledge Base for AI-powered search and issue resolution.
        </p>
      </div>

      {uploadSuccess && (
        <Alert className="border-emerald-300 bg-emerald-50">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">
            Document uploaded and processing started. It will appear in the Knowledge Base once processing is complete.
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-lg border border-kaycoo-teal-light bg-kaycoo-white p-6">
        <DocumentUpload
          onUpload={handleUpload}
          isUploading={isUploading}
          error={uploadError}
          customers={customers}
          customersLoading={customersLoading}
          selectedCustomerId={selectedCustomerId}
          onCustomerChange={setSelectedCustomerId}
        />
      </div>

      {piiResult !== null && (
        <PIIApprovalModal
          items={piiResult.pii_found}
          redactedText={piiResult.redacted_text}
          scanError={piiScanError}
          isRetrying={isScanning}
          onApprove={() => uploadAfterApproval()}
          onCancel={cancelPiiReview}
          onRetry={retryPiiScan}
        />
      )}
    </div>
  )
}
